import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { Agenzia } from '../../pages/store-locator-page/interfaces/agenzia.interface';
import { IRemoteProfile } from '../interfaces/iremote-profile';
import { Networks } from '../models/networks.enum';
import { CruiseTopId } from '../models/cruise-top-id.enum';
import { IHolidayLiveSolution } from '../../booking-flow/holiday/holiday-booking-page/interfaces/iholiday-live-solution';
import * as dayjs from 'dayjs';
import { IHolidayDetail } from '../../booking-flow/holiday/holiday-detail-page/interfaces/iholiday-detail';
import { IHolidayDetailSolution } from '../../booking-flow/holiday/holiday-detail-page/interfaces/iholiday-detail-solution';
import { DemChargeArray } from '../../booking-flow/holiday/holiday-booking-page/utils/holiday-dem-functions';
import { QuotationOperationType } from 'src/app/enums/QuotationOperationType.enum';

interface TemplateData {
  link: string;
  ogTitle: string;
  ogImg: string;
  ogDescription: string;
  logoLink: string;
  email: string;
  address: string;
  naming: string;
  city: string;
}

@Injectable({
  providedIn: 'root'
})
export class DemService {
  private readonly templatesDir = 'assets/templates';
  private httpCache = new Map<string, Observable<any>>();
  private designSystem = 'https://lamp-dev.offertetouroperator.com/projects/zeus/zeus-design-system/styles/partners/oto/assets/e-icons/';
  private layouts = {
    holiday: `${this.templatesDir}/holiday-new-template.html`,
    cruise: `${this.templatesDir}/cruise-new-template.html`,
    holidayWTG: `${this.templatesDir}/holidayTemplateWTG.html`,
    cruiseWTG: `${this.templatesDir}/cruiseTemplateWTG.html`,
    wtgCatalogMail: `${this.templatesDir}/wtg_catalog_email.html`,
    geoCatalogMail: `${this.templatesDir}/geo_catalog_email.html`
  };

  private readonly categorySymbols = {
    STAR: '⭐',
    SPIKE: '🌾',
    KEY: '🔑'
  };

  private demType = null;
  private dataToInsert = null;
  private agencyNetwork = null;

  constructor(
    private _http: HttpClient,
    private _datePipe: DatePipe,
    private _currencyPipe: CurrencyPipe,
    private _titlecasePipe: TitleCasePipe,
  ) {
  }

  getLayout(layoutName: string) {
    if (!this.httpCache.get(this.layouts[layoutName])) {
      this.httpCache.set(this.layouts[layoutName], this._http.get(this.layouts[layoutName], { responseType: 'text' }).pipe(
        shareReplay({ bufferSize: 1, refCount: true })
      ));
    }

    return this.httpCache.get(this.layouts[layoutName]).toPromise();
  }

  formatDem(html: string, dataToInsert: any, typology: 'holiday' | 'cruise', action?: string, agencyNetwork: any = null, isProfileDataFromStoreList: boolean = false) {
    this.agencyNetwork = agencyNetwork;
    this.demType = action;
    if (isProfileDataFromStoreList && dataToInsert?.profileData) {
      // se è stato passato il profileData, procedo a formattarlo seguendo il formato della dem (che utilizza l'interfaccia IRemoteProfile)
      dataToInsert.profileData = this.formatProfileDataForDem(dataToInsert.profileData);
    }
    this.dataToInsert = dataToInsert;
    this.dataToInsert.designSystem = this.designSystem;
    if (!this.dataToInsert.currentQuote?.packageContent?.demCoverImage && this.dataToInsert.shipService?.images?.length > 0) {
      this.dataToInsert.currentQuote.packageContent.demCoverImage = this.dataToInsert.shipService?.images[0]?.image_path;
    }

    // se manca l'oggetto con le info del tour operator, provvedo a recuperarlo dal "details"
    if (!this.dataToInsert.currentQuote.tourOperator) {
      this.dataToInsert.currentQuote.tourOperator = this.dataToInsert.details.tourOperator;
    }

    const layoutDOM = new DOMParser().parseFromString(this.replacePlaceholders(html), 'text/html');

    const itineraryNameElement = layoutDOM.querySelector('#itineraryName');
    if (itineraryNameElement) {
      const originalText = itineraryNameElement.textContent || '';
      const newText = originalText.toString().replace(/,/g, ', ');
      itineraryNameElement.textContent = newText;
    }

    if (typology === 'holiday') {
      return this.formatHolidayDem(layoutDOM);
    } else if (typology === 'cruise') {
      return this.formatCruiseDem(layoutDOM);
    }
  }

  formatHolidayDem(layoutDOM) {
    return new Promise((resolve, reject) => {
      if (this.dataToInsert.currentQuote?.related?.length > 0) {
        this.insertRelated(layoutDOM);
      } else {
        layoutDOM.getElementById('relatedToRepeat')?.remove();
      }
      this.insertCharges(layoutDOM);
      this.insertMeetingPoints(layoutDOM);
      this.insertAgencyMessage(layoutDOM);
      this.insertTourOrAccommodationDescription(layoutDOM);
      this.insertPassengersData(layoutDOM);
      this.insertContactInfo(layoutDOM);
      this.cleanDEM(layoutDOM);

      resolve({
        title: this.demType + ' ' + layoutDOM.getElementById('emailTitleText')?.innerText,
        body: layoutDOM.documentElement.outerHTML,
        loggedEmail: this.dataToInsert?.profileData?.email_1,
        ccnEmail: (this.dataToInsert?.profileData?.email_2 === 'prenotazioni.alltrade@gmail.com') ? this.dataToInsert?.profileData?.email_2 : null,
        masterEmail: this.dataToInsert?.masterProfileData?.email_1,
        customerEmail: this.dataToInsert?.customerForm?.quotation?.camere[0].passeggeri[0]?.email,
        customerZeusEmail: this.dataToInsert.currentQuote?.customer?.find(customer => customer.first === 1)?.email
      });
    });
  }

  formatCruiseDem(layoutDOM: any) {
    return new Promise((resolve, reject) => {
      if (this.dataToInsert.currentQuote?.related?.length > 0) {
        this.insertRelated(layoutDOM);
      } else {
        layoutDOM.getElementById('relatedToRepeat')?.remove();
      }
      this.insertCruiseCharges(layoutDOM);
      this.insertMeetingPoints(layoutDOM);
      this.insertAgencyMessage(layoutDOM);
      this.insertPassengersData(layoutDOM);
      this.insertContactInfo(layoutDOM);
      this.insertExpireDate(layoutDOM);
      this.cleanDEM(layoutDOM);

      resolve({
        title: this.demType + layoutDOM.getElementById('emailTitleText')?.innerText,
        body: layoutDOM.documentElement.outerHTML,
        loggedEmail: this.dataToInsert?.profileData?.email_1,
        ccnEmail: (this.dataToInsert?.profileData?.email_2 === 'prenotazioni.alltrade@gmail.com') ? this.dataToInsert?.profileData?.email_2 : null,
        masterEmail: this.dataToInsert?.masterProfileData?.email_1,
        customerEmail: this.dataToInsert?.customerForm?.quotation?.camere?.passeggeri[0]?.email,
        customerZeusEmail: this.dataToInsert.currentQuote?.customer?.find((customer: any) => customer.first === 1)?.email
      });
    });
  }

  replacePlaceholders(layoutHtml: string, action: string = 'old', agencyLogo?, shipServiceCache?, selectedCabinNumber?, profile?, guestFormObject?, selectedFare?: string, selectedCabinType?: string) {
    const regexpBrackets = new RegExp(/({{)(.*?)(}})/g);
    const placeholders = layoutHtml.match(regexpBrackets);

    if (placeholders && placeholders.length > 0) {
      placeholders.forEach((placeholder: any) => {
        const placeholderSanitized = placeholder.replace(new RegExp(/[{{]|[}}]/g), '');
        if (action === 'new') {
          layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, this.newGetDataToInsertFromPath(agencyLogo, shipServiceCache, selectedCabinNumber, profile, guestFormObject, placeholderSanitized, selectedFare, selectedCabinType) || '');
        } else {
          layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, this.getDataToInsertFromPath(placeholderSanitized) || '');
        }
      });
    }

    const passengers = guestFormObject.map(elem => elem.form);
    const layoutDOM = new DOMParser().parseFromString(layoutHtml, 'text/html');
    this.insertPassengersData(layoutDOM, passengers);
    return new XMLSerializer().serializeToString(layoutDOM);
  }

  controlEmailTemplate(emailObj: { charges: string, htmlBody: string }, template: string): string {
    // regex che trova i charges che vengono mandati tramite live al be che pensa a popolare il campo in questione nel template
    const regexPattern = /##charges##/g;
    const match = regexPattern.exec(template);
    if (match) {
      emailObj.charges =
          `<tr id="charges-row">
            <td width="100%"
              style=" border-width: 0px 0px 1px 0px; border-color:#e0e0e0; border-style:solid;">
              <table width="100%" border="0" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <div style="line-height:16px; height:16px; font-size:16px">&#8202;
                    </div>
                  </td>
                </tr>
                <tr>
                  <td width="276">
                    <div style="line-height:24px;text-align:left;">
                      <span
                        style="color:#101828;font-family:Inter,Arial,sans-serif;font-size:13px;line-height:24px;text-align:left;">
                        ##chargeDescription##
                      </span>
                    </div>
                    </td>
                    <td width="276">
                      <div style="line-height:24px;text-align:right;"><span
                        style="color:#101828;font-family:Inter,Arial,sans-serif;font-size:15px;line-height:24px;text-align:right;">##chargeValue## €</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div style="line-height:16px; height:16px; font-size:16px">&#8202;
                      </div>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>`
    }
    // regex per trovare i blocchi di codice che riguardano il volo
    const regexInbound = /<tr\s+class="flightFrom">[\s\S]*?<\/tr>/g;
    template = template.replace(regexInbound, '');
    const regexOutBound = /<tr\s+class="flightTo">[\s\S]*?<\/tr>/g;
    template = template.replace(regexOutBound, '');
    const regexBoth = /<tr\s+class="flightBoth">[\s\S]*?<\/tr>/g;
    template = template.replace(regexBoth, '');
    emailObj.htmlBody = template;
    return template
  }

  newGetDataToInsertFromPath(agencyLogo, shipServiceCache, selectedCabinNumber, profile, guestFormObject, path, selectedFare: string, selectedCabinType: string): any {
    const designSystem = 'https://lamp-dev.offertetouroperator.com/projects/zeus/zeus-design-system/styles/partners/oto/assets/e-icons/';
    switch (path) {
      case 'currentQuote.customer[0].surname':
        return guestFormObject[0].form.surname || guestFormObject[0].surname;
      case 'currentQuote.customer[0].name':
        return guestFormObject[0].form.name || guestFormObject[0].name;
      case 'currentQuote.cabins[0].cabinNumber':
        return selectedCabinNumber;
      case 'cabinType':
        return selectedCabinType;
      case 'currentQuote.customer.length':
        return (guestFormObject.length).toString();
      case 'currentQuote.packageContent.demCoverImage':
        return shipServiceCache.images[0].image_path;
      case 'profileData.sito':
        return profile.site;
      case 'profileData.tel1':
        return profile.tel1;
      case 'profileData.email1':
        return profile.email1;
      case 'profileData.ragione_sociale':
        return profile.businessName;
      case 'profileData.denominazione':
        return profile.denomination;
      case 'profileData.indirizzo':
        return profile.address;
      case 'profileData.piva':
        return profile.piva;
      case 'profileData.provincia':
        return profile.province;
      case 'profileData.citta':
        return profile.city;
      case 'passengerName':
        return guestFormObject[0].form.name || guestFormObject[0].name;
      case 'passengerSurname':
        return guestFormObject[0].form.surname || guestFormObject[0].surname;
      case 'passengerEmail':
        return guestFormObject[0].form.email || guestFormObject[0].email;
      case 'passengerDate':
        return guestFormObject[0].form.birthDate || guestFormObject[0].dateOfBirth;
      case 'passengerAge':
        return this.calculateAge(guestFormObject[0].form.birthDate) || this.calculateAge(guestFormObject[0].form.dateOfBirth);
      case 'passengerSex':
        return guestFormObject[0].form.gender || guestFormObject[0].sex;
      case 'passengerFiscalCode':
        return guestFormObject[0].form.fiscalCode || guestFormObject[0].fiscalCode;
      // case 'currentQuote.cabins[0].sourceId':
      //   return (this.selectedCabin.id).toString();
      case 'this.getTopOrAgencyLogo()':
        return agencyLogo;
      case 'fare':
        return selectedFare;
      case 'logoTop':
        return `https://cdn.otosrl.com/images/touroperator/mini/${shipServiceCache.info.fk_touroperator}-${shipServiceCache.info.fk_touroperator}.png`
      case 'designSystem':
        return designSystem;
      default:
        break;
    }
  }

  calculateAge(dateOfBirth) {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  }

  replaceShareEmail(layoutHtml: string, templateData : TemplateData) : string {
    const regexpBrackets = new RegExp(/({{)(.*?)(}})/g);
    const placeholders = layoutHtml.match(regexpBrackets);

    if (placeholders && placeholders.length > 0) {
      placeholders.forEach((placeholder: any) => {
        const placeholderSanitized = placeholder.replace(new RegExp(/[{{]|[}}]/g), '');
        if(placeholderSanitized === "title") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.ogTitle);
        if(placeholderSanitized === "subtitle") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.ogDescription);
        if(placeholderSanitized === "img") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.ogImg);
        if(placeholderSanitized === "link") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.link);
        if(placeholderSanitized === "logoLink") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.logoLink);
        if(placeholderSanitized === "address") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.address);
        if(placeholderSanitized === "email") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.email);
        if(placeholderSanitized === "city") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.city);
        if(placeholderSanitized === "agencyName") layoutHtml = layoutHtml.replace(`{{${placeholderSanitized}}}`, templateData.naming);
      });
    }
    return layoutHtml;
  }

  getDataForShareCatalog(path: string) {

  }

  getShipName(): string {
    return this.dataToInsert.currentQuote?.packageContent?.shipName || 'n/a';
  }

  getExternalBookingCode() {
    return this.dataToInsert.currentQuote?.bookingCode || '';
  }

  getDeparturePlaceName() {
    return this.dataToInsert.currentQuote?.operatives?.[0]?.departurePlaceName ?? `${this.dataToInsert?.currentQuote?.packageContent?.departurePlace?.code}${this.dataToInsert?.currentQuote?.packageContent?.departurePlace?.code ? ' - ' : ''}${this.dataToInsert?.currentQuote?.packageContent?.departurePlace?.name || ''}`;
  }

  getStopOverPlaceName() {
    return (this.dataToInsert.currentQuote?.operatives?.[0]?.stopOver) ? `${this.dataToInsert.currentQuote.operatives[0].stopOver.code}${this.dataToInsert.currentQuote.operatives[0].stopOver.code ? ' - ' : ''}${this.dataToInsert.currentQuote.operatives[0].stopOver.name || ''}` : '';
  }

  getArrivalPlaceName() {
    return this.dataToInsert.currentQuote?.operatives?.[0]?.arrivalPlaceName ?? `${this.dataToInsert?.currentQuote?.packageContent?.arrivalPlace?.code}${this.dataToInsert?.currentQuote?.packageContent?.arrivalPlace?.code ? ' - ' : ''}${this.dataToInsert?.currentQuote?.packageContent?.arrivalPlace?.name || ''}`;
  }

  getDataToInsertFromPath(path: string) {
    if (path === 'currentQuote.createdAt') {
      return this._datePipe.transform(this.dataToInsert.currentQuote?.createdAt, 'dd/MM/yyyy HH:mm');
    }
    if (path === 'currentQuote.components[0].city') {
      return this.dataToInsert.currentQuote?.components[0]?.city;
    }
    if (path === 'currentQuote.packageContent.demCoverImage') {
      return this.dataToInsert.currentQuote?.packageContent?.demCoverImage || '';
    }
    if (path === 'currentQuote.packageContent.departureDateFrom') {
      return this._datePipe.transform(this.dataToInsert.currentQuote?.packageContent?.departureDateFrom, 'dd/MM/yyyy');
    }
    if (path === 'currentQuote.packageContent.departureDateTo') {
      return this._datePipe.transform(this.dataToInsert.currentQuote?.packageContent?.departureDateTo, 'dd/MM/yyyy');
    }
    if (path === 'currentQuote.components[0].description') {
      return this.dataToInsert.currentQuote?.components[0]?.description;
    }
    if (path === 'currentQuote.cabins[0].description') {
      return this.dataToInsert.currentQuote?.cabins[0]?.description;
    }
    if (path === 'currentQuote.cabins[0].cabinNumber') {
      return this.dataToInsert.currentQuote?.cabins[0]?.cabinNumber;
    }
    if (path === 'currentQuote.cabins[0].sourceId') {
      return this.dataToInsert.currentQuote?.cabins[0]?.sourceId;
    }
    if (path === 'currentQuote.customer[0].surname') {
      return this.dataToInsert.currentQuote?.customer?.find(customer => customer.first === 1)?.surname;
    }
    if (path === 'currentQuote.customer[0].name') {
      return this.dataToInsert.currentQuote?.customer?.find(customer => customer.first === 1)?.name;
    }
    if (path === 'currentQuote.selectedLiveSlim.consumerPrice') {
      return (this.dataToInsert.currentQuote?.selectedLiveSlim?.consumerPrice) ? Number(this.dataToInsert.currentQuote?.selectedLiveSlim?.consumerPrice) : '';
    }

    if (path === 'currentQuote.packageContent.packageDetail.category.typology') {
      let toReturn = '';
      if (this.dataToInsert.currentQuote?.packageContent?.packageDetail?.category?.description?.includes('/')) {
        toReturn = this.categorySymbols[this.dataToInsert.currentQuote?.packageContent?.packageDetail?.category?.typology];
      } else {
        for (let i = 0; i < Number(this.dataToInsert.currentQuote?.packageContent?.packageDetail?.category?.description); i++) {
          toReturn += this.categorySymbols[this.dataToInsert.currentQuote?.packageContent?.packageDetail?.category?.typology];
        }
      }
      return toReturn;
    }


    if (path.includes('()')) {
      // tslint:disable-next-line:no-eval
      return eval(path);
    }

    return path.split('.').reduce((prev: { [x: string]: any; }, curr: string | number) => {
      return prev ? prev[curr] : '';
    }, this.dataToInsert || self);
  }

  cleanDEM(layoutDOM: Document) {
    const flightFrom = layoutDOM.querySelectorAll('.flightFrom');
    const flightTo = layoutDOM.querySelectorAll('.flightTo');
    const flightBoth = layoutDOM.querySelectorAll('.flightBoth');
    // per leggibilità ho messo in due variabili questi dati
    const components = this.dataToInsert.currentQuote?.components;
    const direction = components?.length > 0 ? components[0].direction : undefined;

    if (direction) {
      if (direction === 'OutBound') {
        flightBoth.forEach(el => el.remove());
        flightTo.forEach(el => el.remove());
      } else if (direction === 'InBound') {
        flightBoth.forEach(el => el.remove());
        flightFrom.forEach(el => el.remove());
      } else {
        flightTo.forEach(el => el.remove());
        flightFrom.forEach(el => el.remove());
      }
    } else {
      flightBoth.forEach(el => el.remove());
      flightFrom.forEach(el => el.remove());
      flightTo.forEach(el => el.remove());
    }

    const headerExternalBookingCodeNode = layoutDOM.getElementById('headerExternalBookingCode');
    if (headerExternalBookingCodeNode && !this.getExternalBookingCode()) {
      // verifico se è presente il codice pratica esterna e in tal caso, se NON presente, rimuovo il relativo nodo DOM sull'header dedicato al codice pratica esterna
      headerExternalBookingCodeNode.remove();
    }
    const dinnerInfoNode = layoutDOM.getElementById('dinnerInfo');
    const dinnerInfoRecapNode = layoutDOM.getElementById('dinnerInfoRecap');
    if (dinnerInfoNode && dinnerInfoRecapNode && !this.dataToInsert.currentQuote?.components[0]?.description) {
      // verifico se sono presenti informazioni riguardo il turno cena altrimenti, elimino il nodo DOM della parte del turno cena sulla email
      dinnerInfoNode.remove();
      dinnerInfoRecapNode.remove();
    }
    const departure = layoutDOM.getElementById('departure');
    const departureFrom = layoutDOM.getElementById('departureFrom');
    const sololand = layoutDOM.getElementById('sololand');
    if (departure && departureFrom && sololand) {
      if (this.dataToInsert.currentQuote?.packageContent?.departurePlace) {
        departure.style.display = 'block';
        departureFrom.style.display = 'block';
        sololand.style.display = 'none';
      } else {
        departure.style.display = 'none';
        departureFrom.style.display = 'none';
        sololand.style.display = 'block';
      }
    }
    const colSito = layoutDOM.getElementById('colAgencyData__website');
    const colContatti = layoutDOM.getElementById('colAgencyData__tel');
    if (!this.dataToInsert.profileData.sito || this.dataToInsert.profileData.sito.trim().length === 0) {
      colSito.remove();
      // tslint:disable-next-line: deprecation
      (colContatti as HTMLTableElement).align = 'center';
    }
    if (
      (!this.dataToInsert.profileData?.tel1 || this.dataToInsert.profileData.tel1.trim().length === 0) &&
      (!this.dataToInsert.profileData?.tel2 || this.dataToInsert.profileData.tel2.trim().length === 0) &&
      (!this.dataToInsert.profileData?.email1 || this.dataToInsert.profileData.email1.trim().length === 0) &&
      (!this.dataToInsert.profileData?.email2 || this.dataToInsert.profileData.email2.trim().length === 0)
    ) {
      colContatti.remove();
      if (colSito) {
        // tslint:disable-next-line: deprecation
        (colSito as HTMLTableElement).align = 'center';
      }
    }
    if (
      (!this.dataToInsert.profileData.tel1 || this.dataToInsert.profileData.tel1.trim().length === 0) &&
      (!this.dataToInsert.profileData.tel2 || this.dataToInsert.profileData.tel2.trim().length === 0)
    ) {
      layoutDOM.getElementById('colTel')?.remove();
    }
    if (
      (!this.dataToInsert.profileData.email1 || this.dataToInsert.profileData.email1.trim().length === 0) &&
      (!this.dataToInsert.profileData.email2 || this.dataToInsert.profileData.email2.trim().length === 0)
    ) {
      layoutDOM.getElementById('colMail')?.remove();
    }

    const stopOver = layoutDOM.getElementById('stopOver');
    if (stopOver) {
      stopOver.style.display = (this.dataToInsert.currentQuote?.operatives?.[0]?.stopOver) ? 'block' : 'none';
    }
    const arrival = layoutDOM.getElementById('arrival');
    const arrivalPlace = layoutDOM.getElementById('arrivalPlace');
    if (arrival && arrivalPlace) {
      if (this.dataToInsert.currentQuote?.operatives?.length || this.dataToInsert?.currentQuote?.packageContent?.arrivalPlace?.id) {
        arrival.style.display = 'block';
        arrivalPlace.style.display = 'block';
      } else {
        arrival.style.display = 'none';
        arrivalPlace.style.display = 'none';
      }
    }
  }

  getTopOrAgencyLogo(): string {
    if (this.dataToInsert?.currentQuote?.packageContent?.logo) {
      return this.dataToInsert.currentQuote.packageContent.logo;
    } else {
      if (this.dataToInsert?.masterProfileData?.logo?.trim() && !this.dataToInsert?.profileData?.isMaster && !this.dataToInsert?.profileData?.isAgencySite) {
        // se presente il logo dell'agenzia master e i dati di profilo dell'agenzia corrispondono ad una filiale (NO agenzia master), allora recupero il logo da "masterProfileData"
        this.dataToInsert.profileData.logo = JSON.parse(JSON.stringify(this.dataToInsert.masterProfileData.logo));
      }
      if (!this.dataToInsert?.profileData?.logo?.trim() || !String(this.dataToInsert?.profileData?.logo?.trim()).startsWith('http')) {
        this.dataToInsert.profileData.logo = 'https://cdn.otosrl.com/images/clienti/';
        if (this.agencyNetwork?.networkId === Networks.GEO) {
          this.dataToInsert.profileData.logo += 'logo_geo_otofe_dem.png';
        } else if (this.agencyNetwork?.networkId === Networks.WTG || this.agencyNetwork?.networkId === Networks.WTG_MASTER) {
          this.dataToInsert.profileData.logo += 'logo_wel_otofe_dem.png';
        } else {
          this.dataToInsert.profileData.logo += 'logo_otofe_dem.png';
        }
      } else if (this.dataToInsert?.profileData?.logo?.trim() && String(this.dataToInsert?.profileData?.logo?.trim()).startsWith('http')) {
        return this.dataToInsert.profileData.logo.trim();
      }
      return `https://cdn.otosrl.com/images/clienti/${this.dataToInsert.profileData.logo.trim()}`;
    }
  }

  getAgencyLogo(): string {
    if (this.dataToInsert?.masterProfileData?.logo?.trim() && !this.dataToInsert?.profileData?.isMaster && !this.dataToInsert?.profileData?.isAgencySite) {
      // se presente il logo dell'agenzia master e i dati di profilo dell'agenzia corrispondono ad una filiale (NO agenzia master), allora recupero il logo da "masterProfileData"
      this.dataToInsert.profileData.logo = JSON.parse(JSON.stringify(this.dataToInsert.masterProfileData.logo));
    }
    if (!this.dataToInsert?.profileData?.logo?.trim() || !String(this.dataToInsert?.profileData?.logo?.trim()).startsWith('http')) {
      this.dataToInsert.profileData.logo = 'https://cdn.otosrl.com/images/clienti/';
      if (this.agencyNetwork?.networkId === Networks.GEO) {
        this.dataToInsert.profileData.logo += 'logo_geo_otofe_dem.png';
      } else if (this.agencyNetwork?.networkId === Networks.WTG || this.agencyNetwork?.networkId === Networks.WTG_MASTER) {
        this.dataToInsert.profileData.logo += 'logo_wel_otofe_dem.png';
      } else {
        this.dataToInsert.profileData.logo += 'logo_otofe_dem.png';
      }
    } else if (this.dataToInsert?.profileData?.logo?.trim() && String(this.dataToInsert?.profileData?.logo?.trim()).startsWith('http')) {
      return this.dataToInsert.profileData.logo.trim();
    }
    return `https://cdn.otosrl.com/images/clienti/${this.dataToInsert.profileData.logo.trim()}`;
  }

  insertCharges(layoutDOM: Document) {
    const chargesNode = layoutDOM.getElementById('chargesToRepeat');
    const chargesLocallyNode = layoutDOM.getElementById('chargesLocallyToRepeat');

    const chargesToPay = this.dataToInsert.currentQuote.charges.filter(charge => charge.payableLocally !== 1)
      .sort((a, b) => {
        return (a.progressive < b.progressive) ? -1 : (a.progressive > b.progressive) ? 1 : 0;
      });

    const chargesPayableLocally = this.dataToInsert.currentQuote.charges.filter(charge => charge.payableLocally === 1)
      .sort((a, b) => {
        return (a.progressive < b.progressive) ? -1 : (a.progressive > b.progressive) ? 1 : 0;
      });


    if (chargesNode) {
      const chargeHTML = chargesNode.innerHTML.replace('id="chargesToRepeat"', '');

      chargesToPay.forEach((charge) => {
        let currentChargeHtml = chargeHTML;

        currentChargeHtml = currentChargeHtml.replace(`[[charge.description]]`, `${charge.description}`);
        currentChargeHtml = currentChargeHtml.replace(`[[charge.value]]`, charge.included === 1 && charge.value === 0 ? 'Gratuito' : this._currencyPipe.transform(charge.value, '€', 'symbol', '1.2-2', 'it'));

        chargesNode.insertAdjacentHTML('beforebegin', currentChargeHtml);
      });

      chargesNode.remove();
    }

    if (chargesLocallyNode) {
      const chargeHTML = chargesLocallyNode.innerHTML.replace('id="chargesLocallyToRepeat"', '');

      if (chargesPayableLocally.length === 0) {
        layoutDOM.getElementById('locallyTable').remove();
      } else {
        chargesPayableLocally.forEach((charge) => {
          let currentChargeHtml = chargeHTML;

          currentChargeHtml = currentChargeHtml.replace(`[[chargeLocal.description]]`, `${charge.description}`);
          currentChargeHtml = currentChargeHtml.replace(`[[chargeLocal.value]]`, charge.included === 1 && charge.value === 0 ? 'Gratuito' : this._currencyPipe.transform(charge.value, '€', 'symbol', '1.2-2', 'it'));

          chargesLocallyNode.insertAdjacentHTML('beforebegin', currentChargeHtml);
        });

        chargesLocallyNode.remove();
      }
    }
  }

  insertCruiseCharges(layoutDOM: Document) {
    const chargesNode = layoutDOM.getElementById('chargesToRepeat');
    let allCharges = [];

    if (this.dataToInsert?.currentQuote?.tourOperator?.id === CruiseTopId.COSTA || this.dataToInsert?.currentQuote?.tourOperator?.id === CruiseTopId.MSC) {
      // se si tratta di cruise Costa o MSC, recupero solo alcuni charges/details che servono da mostrare nel riepilogo in modo formattato secondo la predisposizione della dem
      const portTaxesFromLiveSlim = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.filter((detail) => detail.WebCode === 'PCH') || [];
      const insurancesFromLiveSlim = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.filter((detail) => detail.WebCode === 'INS') || [];
      const cabinCharge = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.find((detail) => (detail.WebCode === 'CAB'));

      allCharges.push({
        description: 'Quota base',
        value: this.dataToInsert?.currentQuote?.selectedLiveSlim?.basePrice || cabinCharge?.GrossPrice || 0
      });
      if (this.dataToInsert?.currentQuote?.tourOperator?.id == CruiseTopId.MSC && this.dataToInsert?.currentQuote?.selectedLiveSlim?.onaPrice) {
        allCharges.push({
          description: 'Tasse',
          value: this.dataToInsert?.currentQuote?.selectedLiveSlim?.onaPrice
        });
      }
      if (portTaxesFromLiveSlim?.length) {
        for (let i = 0; i < portTaxesFromLiveSlim.length; i++) {
          allCharges.push({
            description: portTaxesFromLiveSlim[i].Description,
            value: Number(portTaxesFromLiveSlim[i].GrossPrice)
          });
        }
      }
      if (insurancesFromLiveSlim?.length) {
        for (let i = 0; i < insurancesFromLiveSlim.length; i++) {
          allCharges.push({
            description: insurancesFromLiveSlim[i].Description,
            value: Number(insurancesFromLiveSlim[i].GrossPrice)
          });
        }
      }
    } else {
      // ... altrimenti, se NON è una cruise Costa ed MSC, allora, recupero tutti i charges/details ritornati in modo da mostrarli nel riepilogo
      allCharges = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.map((detail) => {
        detail.description = detail.Description;
        detail.value = detail.GrossPrice;
        return detail;
      })?.sort((ch1: any, ch2: any) => ch1.value - ch2.value) || [];
    }

    if (chargesNode) {
      const chargeHTML = chargesNode.innerHTML.replace('id="chargesToRepeat"', '');

      allCharges.forEach((charge: any) => {
        let currentChargeHtml = chargeHTML;

        currentChargeHtml = currentChargeHtml.replace(`[[charge.description]]`, `${charge.description}`);
        currentChargeHtml = currentChargeHtml.replace(`[[charge.value]]`, (charge.value === 0) ? 'Incluso' : this._currencyPipe.transform(charge.value, '€', 'symbol', '1.2-2', 'it'));

        chargesNode.insertAdjacentHTML('afterend', currentChargeHtml);
      });

      chargesNode.remove();
    }
  }

  insertMeetingPoints(layoutDOM: Document) {
    const meetingPointsNode = layoutDOM.getElementById('meetingPointsToRepeat');

    if (meetingPointsNode) {
      const chargeHTML = meetingPointsNode.innerHTML.replace('id="chargesToRepeat"', '');
      if (this.dataToInsert.currentQuote.packageContent.experience.hotelPickup === 0) {
        this.dataToInsert.currentQuote.packageContent.experience.meetingPoints.forEach((meetingPoint) => {
          let currentMeetingPointHtml = chargeHTML;

          currentMeetingPointHtml = currentMeetingPointHtml.replace(`[[meetingPoint.place]]`, `<small>${meetingPoint.destination.name}</small> <br /> ${meetingPoint.destinationName}`);

          meetingPointsNode.insertAdjacentHTML('afterend', currentMeetingPointHtml);
        });
      } else {
        let currentMeetingPointHtml = chargeHTML;
        currentMeetingPointHtml = currentMeetingPointHtml.replace(`[[meetingPoint.place]]`, `Pickup in hotel`);
        meetingPointsNode.insertAdjacentHTML('afterend', currentMeetingPointHtml);
      }

      meetingPointsNode.remove();
    }
  }

  insertRelated(layoutDOM: Document) {
    const chargesNode = layoutDOM.getElementById('relatedToRepeat');

    if (chargesNode) {
      const chargeHTML = chargesNode.innerHTML.replace('id="relatedToRepeat"', '');

      this.dataToInsert.currentQuote?.related?.forEach((related) => {
        let currentRelatedHtml = chargeHTML;

        currentRelatedHtml = currentRelatedHtml.replace(`[[related.description]]`, `${related.packageContent.title}`);
        currentRelatedHtml = currentRelatedHtml.replace(`[[related.value]]`, this._currencyPipe.transform(related.value, '€', 'symbol', '1.2-2', 'it'));

        chargesNode.insertAdjacentHTML('afterend', currentRelatedHtml);
      });

      chargesNode.remove();
    }
  }

  insertAgencyMessage(layoutDOM: Document) {
    const hasOndemand = this.dataToInsert.currentQuote.charges?.filter(t => t.onDemand === 1)?.length > 0;
    if (hasOndemand) {
      const onDemandNode = layoutDOM.getElementById('onDemand');
      if (onDemandNode) {
        onDemandNode.innerHTML = 'Attenzione. La sua pratica contiene dei servizi su richiesta.';
      }
    }
    const agencyMessageNode = layoutDOM.getElementById('agencyMessage');
    if (agencyMessageNode && this.dataToInsert.currentQuote.packageContent.layoutMessage) {
      agencyMessageNode.innerHTML = this.dataToInsert.currentQuote.packageContent.layoutMessage;
    } else {
      layoutDOM.getElementById('agencyMessageBlock')?.remove();
      layoutDOM.getElementById('agencyMessageDivider')?.remove();
    }
  }

  insertTourOrAccommodationDescription(layoutDOM: Document) {
    const textToAppendNode = layoutDOM.getElementById('textToAppend');
    const previewTextToAppendNode = layoutDOM.getElementById('previewTextToAppend');
    const textToAppend = this.getText() || '';
    if (textToAppendNode) {
      textToAppendNode.innerHTML = textToAppend;
    }
    if (previewTextToAppendNode) {
      previewTextToAppendNode.innerHTML = textToAppend;
    }
  }

  insertFlightsData(layoutDOM: Document, operatives: any): Document {
    const outBoundDirect = layoutDOM.getElementById('out-bound-direct');
    const inBoundDirect = layoutDOM.getElementById('in-bound-direct');

    if (inBoundDirect && outBoundDirect && operatives) {
      const outBoundDirectCopy = document.createElement('table');
      const inBoundDirectCopy = document.createElement('table');
      // tslint:disable-next-line: no-string-literal
      [...outBoundDirect.parentElement.children['out-bound-direct'].attributes].forEach(attribute => {
        outBoundDirectCopy.setAttribute(attribute.name, attribute.value);
      });
      outBoundDirectCopy.innerHTML = outBoundDirect.innerHTML;

      [...inBoundDirect.parentElement.children['in-bound-direct'].attributes].forEach(attribute => {
        inBoundDirectCopy.setAttribute(attribute.name, attribute.value);
      });
      inBoundDirectCopy.innerHTML = outBoundDirect.innerHTML;

      const outBoundData = {
        departurePlace: outBoundDirectCopy.getElementsByClassName('departure-place')[0],
        departureTime: outBoundDirectCopy.getElementsByClassName('departure-time')[0],
        arrivalPlace: outBoundDirectCopy.getElementsByClassName('arrival-place')[0],
        arrivalTime: outBoundDirectCopy.getElementsByClassName('arrival-time')[0]
      };

      const inBoundData = {
        departurePlace: inBoundDirectCopy.getElementsByClassName('departure-place')[0],
        departureTime: inBoundDirectCopy.getElementsByClassName('departure-time')[0],
        arrivalPlace: inBoundDirectCopy.getElementsByClassName('arrival-place')[0],
        arrivalTime: inBoundDirectCopy.getElementsByClassName('arrival-time')[0]
      };

      outBoundData.departurePlace.innerHTML = `${operatives.outBound.departurePlaceName}`;
      outBoundData.departurePlace.className.replace('departure-place', '');

      outBoundData.departureTime.innerHTML = `PARTENZA: ${operatives.outBound.departureTime}`;
      outBoundData.departureTime.className.replace('departure-time', '');

      outBoundData.arrivalPlace.innerHTML = `${operatives.outBound.arrivalPlaceName}`;
      outBoundData.arrivalPlace.className.replace('arrival-place', '');

      outBoundData.arrivalTime.innerHTML = `ARRIVO: ${operatives.outBound.arrivalTime}`;
      outBoundData.arrivalTime.className.replace('arrival-time', '');

      outBoundDirectCopy.className.replace('out-bound-direct', '');
      outBoundDirectCopy.removeAttribute('id');
      outBoundDirect.insertAdjacentElement('beforebegin', outBoundDirectCopy);

      outBoundDirect.remove();


      inBoundData.departurePlace.innerHTML = `${operatives.inBound.departurePlaceName}`;
      inBoundData.departurePlace.className.replace('departure-place', '');

      inBoundData.departureTime.innerHTML = `PARTENZA: ${operatives.inBound.departureTime}`;
      inBoundData.departureTime.className.replace('departure-time', '');

      inBoundData.arrivalPlace.innerHTML = `${operatives.inBound.arrivalPlaceName}`;
      inBoundData.arrivalPlace.className.replace('arrival-place', '');

      inBoundData.arrivalTime.innerHTML = `ARRIVO: ${operatives.inBound.arrivalTime}`;
      inBoundData.arrivalTime.className.replace('arrival-time', '');

      inBoundDirectCopy.className.replace('in-bound-direct', '');
      inBoundDirectCopy.removeAttribute('id');
      inBoundDirect.insertAdjacentElement('beforebegin', inBoundDirectCopy);

      inBoundDirect.remove();
    } else {
      const elements = layoutDOM.querySelectorAll(`.block-flights`);
      elements.forEach(el => el.remove());
    }
    return layoutDOM;
  }

  insertPassengersData(
    layoutDOM: Document,
    quotationPassengers: any[] = this.dataToInsert.currentQuote.customer
  ): Document {
    try {
      this.renderFirstPassenger(layoutDOM, quotationPassengers[0]);
      this.renderOtherPassengers(layoutDOM, quotationPassengers.slice(1));
      return layoutDOM;
    } catch (error) {
      console.error('Errore durante insertPassengersData:', error);
      throw error;
    }
  }

  renderFirstPassenger(layoutDOM: Document, firstPassenger: any): Document {
    const passengerDataBlock = layoutDOM.getElementById('firstPassenger');

    if (passengerDataBlock && firstPassenger) {
      const passengerDataBlockCopy = document.createElement('tr');
      // tslint:disable-next-line: no-string-literal
      [...passengerDataBlock.parentElement.children['firstPassenger'].attributes].forEach(attribute => {
        passengerDataBlockCopy.setAttribute(attribute.name, attribute.value);
      });
      passengerDataBlockCopy.innerHTML = passengerDataBlock.innerHTML;

      const passengerData = {
        name: passengerDataBlockCopy.getElementsByClassName('passengerData__name')[0],
        containerEmail: passengerDataBlockCopy.getElementsByClassName('container_passengerData__email')[0],
        email: passengerDataBlockCopy.getElementsByClassName('passengerData__email')[0],
        birth: passengerDataBlockCopy.getElementsByClassName('passengerData__birth')[0],
        containerPhone: passengerDataBlockCopy.getElementsByClassName('container_passengerData__phone')[0],
        phone: passengerDataBlockCopy.getElementsByClassName('passengerData__phone')[0],
        containerFiscod: passengerDataBlockCopy.getElementsByClassName('container_passengerData__fiscod')[0],
        fiscod: passengerDataBlockCopy.getElementsByClassName('passengerData__fiscod')[0],
      };

      passengerData.name.innerHTML = `${this._titlecasePipe.transform(firstPassenger.name)} ${this._titlecasePipe.transform(firstPassenger.surname)}`;
      passengerData.name.className.replace('passengerData__name', '');

      const dateOfBirth = this._datePipe.transform(firstPassenger?.dateOfBirth || firstPassenger?.birthDate, 'shortDate');
      const age = firstPassenger?.birthDate && this.calculateAge(firstPassenger?.birthDate);
      passengerData.birth.innerHTML = `${dateOfBirth} - (${firstPassenger.age || age} anni) ${(firstPassenger?.sex || firstPassenger?.gender) ? '- ' + (firstPassenger?.sex?.toUpperCase() || firstPassenger?.gender?.toUpperCase()) : ''}`;
      passengerData.birth.className.replace('passengerData__birth', '');
      if (!firstPassenger.email) {
        passengerData.containerEmail.remove();
      } else {
        passengerData.email.innerHTML = `<a href="mailto:${firstPassenger.email}">${firstPassenger.email}</a>`;
        passengerData.email.className.replace('passengerData__email', '');
      }
      if (!firstPassenger.phoneNumber) {
        passengerData.containerPhone.remove();
      } else {
        passengerData.phone.innerHTML = `<a href="tel:${firstPassenger.phoneNumber}">${firstPassenger.phoneNumber}</a>`;
        passengerData.phone.className.replace('passengerData__phone', '');
      }

      if (!firstPassenger.fiscalCode) {
        passengerData.containerFiscod.remove();
      } else {
        passengerData.fiscod.innerHTML = `${firstPassenger.fiscalCode.toUpperCase()}`;
        passengerData.fiscod.className.replace('passengerData__fiscod', '');
      }

      passengerDataBlockCopy.className.replace('firstPassenger', '');
      passengerDataBlockCopy.removeAttribute('id');
      passengerDataBlock.insertAdjacentElement('beforebegin', passengerDataBlockCopy);

      passengerDataBlock.remove();
    }
    return layoutDOM;
  }

  renderOtherPassengers(layoutDOM: Document, otherPassengers: any[]): Document {
    const passengersDataBlock = layoutDOM.getElementById('tableOtherPassengers');
    if(!otherPassengers.length) {
      passengersDataBlock.remove();
      return layoutDOM;
    }

    if (passengersDataBlock) {
      otherPassengers.forEach((customer, customerIndex) => {

        const passengersDataBlockCopy = document.createElement('tr');
        // tslint:disable-next-line: no-string-literal
        [...passengersDataBlock.parentElement.children['tableOtherPassengers'].attributes].forEach(attribute => {
          passengersDataBlockCopy.setAttribute(attribute.name, attribute.value);
        });
        passengersDataBlockCopy.innerHTML = passengersDataBlock.innerHTML;

        const passengerData = {
          name: passengersDataBlockCopy.getElementsByClassName('otherPassengerData__name')[0],
          email: passengersDataBlockCopy.getElementsByClassName('otherPassengerData__email')[0],
          birth: passengersDataBlockCopy.getElementsByClassName('otherPassengerData__birth')[0],
          numberPassenger: passengersDataBlockCopy.getElementsByClassName('otherPassengerData__numberPassenger')[0]
        };

        passengerData.numberPassenger.innerHTML = `Passeggero ${customerIndex + 2}`;
        passengerData.numberPassenger.className.replace('otherPassengerData__numberPassenger', '');

        passengerData.name.innerHTML = `${this._titlecasePipe.transform(customer.name)} ${this._titlecasePipe.transform(customer.surname)}`;
        passengerData.name.className.replace('otherPassengerData__name', '');

        const dateOfBirth = this._datePipe.transform(customer?.dateOfBirth || customer?.birthDate, 'shortDate');
        const age = customer?.birthDate && this.calculateAge(customer?.birthDate);
        passengerData.birth.innerHTML = `${dateOfBirth} - (${customer?.age || age} anni) ${(customer?.sex || customer?.gender) ? '- ' + (customer?.sex?.toUpperCase() || customer?.gender) : ''}`;
        passengerData.birth.className.replace('otherPassengerData__birth', '');

        if (!customer?.email || customer?.email === null) {
          passengerData.email.remove();
        } else {
          passengerData.email.innerHTML = `<a href="mailto:${customer.email}">${customer.email}</a>`;
          passengerData.email.className.replace('otherPassengerData__email', '');
        }

        //passengersDataBlockCopy.className.replace('firstPassenger', '');
        passengersDataBlockCopy.removeAttribute('id');
        passengersDataBlock.insertAdjacentElement('beforebegin', passengersDataBlockCopy);

        passengersDataBlock.remove();
      })
    }
    return layoutDOM;
  }

  insertContactInfo(layoutDOM: Document) {
    let contactInfoData = '';
    if (this.dataToInsert.profileData.email_1 && this.dataToInsert.profileData.email_1?.trim().length > 0) {
      contactInfoData += `<a href="mailto:${this.dataToInsert.profileData.email_1}" target="_blank" rel="noopener noreferrer" style="mso-line-height-rule: exactly;-ms-text-size-adjust: 100%;-webkit-text-size-adjust: 100%;font-weight: normal;text-decoration: underline;">
        ${this.dataToInsert.profileData.email_1}
      </a>`;
    }

    if (this.dataToInsert.profileData.email_2 && this.dataToInsert.profileData.email_2?.trim().length > 0) {
      contactInfoData += ((this.dataToInsert.profileData.email_1 && this.dataToInsert.profileData.email_1?.trim().length > 0) ? ', ' : '') + `<a href="mailto:${this.dataToInsert.profileData.email_2}" target="_blank" rel="noopener noreferrer" style="mso-line-height-rule: exactly;-ms-text-size-adjust: 100%;-webkit-text-size-adjust: 100%;font-weight: normal;text-decoration: underline;">
        ${this.dataToInsert.profileData.email_2}
      </a>`;
    }

    if (
      (this.dataToInsert.profileData.tel_1 && this.dataToInsert.profileData.tel_1?.trim().length > 0) ||
      (this.dataToInsert.profileData.tel_2 && this.dataToInsert.profileData.tel_2?.trim().length > 0)
    ) {
      contactInfoData += (this.dataToInsert.profileData.email_1 || this.dataToInsert.profileData.email_1 ? ', ' : '') + 'Tel: ';
      if (this.dataToInsert.profileData.tel_1 && this.dataToInsert.profileData.tel_1?.trim().length > 0) {
        contactInfoData += `<a href="tel:${this.dataToInsert.profileData.tel_1}" style="mso-line-height-rule: exactly;-ms-text-size-adjust: 100%;-webkit-text-size-adjust: 100%;font-weight: normal;text-decoration: underline;">
          ${this.dataToInsert.profileData.tel_1}
        </a>`;
      }
      if (this.dataToInsert.profileData.tel_2 && this.dataToInsert.profileData.tel_2?.trim().length > 0) {
        contactInfoData += ((this.dataToInsert.profileData.tel_1 && this.dataToInsert.profileData.tel_1?.trim().length > 0) ? ', ' : '') + `<a href="tel:${this.dataToInsert.profileData.tel_2}" style="mso-line-height-rule: exactly;-ms-text-size-adjust: 100%;-webkit-text-size-adjust: 100%;font-weight: normal;text-decoration: underline;">
          ${this.dataToInsert.profileData.tel_2}
        </a>`;
      }
    }
    layoutDOM.getElementById('contactInfo').innerHTML = contactInfoData;
  }

  insertExpireDate(layoutDOM: any) {
    if (this.dataToInsert.currentQuote?.expirationDate) {
      const node = layoutDOM.getElementById('expireDate');
      node.innerHTML = 'Data scadenza opzione:' + this._datePipe.transform(this.dataToInsert.currentQuote?.expirationDate, 'dd/MM/yyyy');
    } else {
      layoutDOM.getElementById('expireDate')?.remove();
    }
  }

  getTotalValue() {
    let totalValue = 0;

    this.dataToInsert.currentQuote.charges.forEach(charge => {
      totalValue += charge.payableLocally !== 1 ? Number(charge.value) : 0;
    });

    this.dataToInsert.currentQuote?.related?.forEach(related => {
      totalValue += related.value;
    });
    return this._currencyPipe.transform(totalValue, '€', 'symbol', '1.2-2', 'it');
  }

  getCruiseTotalValue() {
    const totalPriceCharge = this.dataToInsert?.currentQuote?.selectedLiveSlim?.details?.find((detail) => (detail.WebCode === 'TOT'));

    let totalValue = 0;
    if (this.dataToInsert?.currentQuote?.selectedLiveSlim?.consumerPrice) {
      totalValue = this.dataToInsert.currentQuote.selectedLiveSlim.consumerPrice;
    } else if (!!totalPriceCharge) {
      totalValue = totalPriceCharge?.GrossPrice;
    }
    return this._currencyPipe.transform(totalValue, '€', 'symbol', '1.2-2', 'it');
  }

  getTotalLocallyValue() {
    let totalValue = 0;

    this.dataToInsert.currentQuote.charges.forEach(charge => {
      totalValue += charge.payableLocally === 1 ? Number(charge.value) : 0;
    });

    return this._currencyPipe.transform(totalValue, '€', 'symbol', '1.2-2', 'it');
  }

  getTreatment() {
    return this.dataToInsert.currentQuote?.packageContent?.treatment?.name ? this.dataToInsert.currentQuote.packageContent.treatment.name : '' +
      (this.dataToInsert.currentQuote?.packageContent?.treatment?.code ? '(' + this.dataToInsert.currentQuote.packageContent.treatment.code + ')' : '');
  }

  getText() {
    const regexp = /(<br>|<br\/>|<br\ \/>)/gi;
    let textToReturn: string;

    switch (this.dataToInsert.currentQuote?.packageContent?.typology) {
      case 'Holiday':
        textToReturn = this.dataToInsert.currentQuote.packageContent.accommodation.accommodationTexts[0]?.description;
        break;
      case 'Tour':
        textToReturn = this.dataToInsert.currentQuote.packageContent.tour.details[0]?.description;
        break;
      case 'Cruise':
        textToReturn = this.dataToInsert.currentQuote.packageContent.itinerary.parent.name + ' - ' + this.dataToInsert.currentQuote.packageContent.itinerary.name;
        break;
      case 'Experience':
        const text = this.dataToInsert.currentQuote.packageContent.experience.experiencePlan.texts.find(text => text.progressive === 1);
        textToReturn = `<b>${text?.title}:</b> <br /> ${text?.description}`;
        break;
      default:
        textToReturn = null;
    }

    if (textToReturn) {
      const tmpDiv = document.createElement('div');
      textToReturn = textToReturn.replace(regexp, '\n');
      textToReturn = textToReturn.substr(0, 500);
      const idxToDelete = textToReturn.lastIndexOf(' ');
      tmpDiv.innerHTML = textToReturn.substr(0, idxToDelete) + '...';
      tmpDiv.innerHTML = tmpDiv.textContent.replace('\n', '<br />');
      return tmpDiv.innerHTML;
    }
  }

  getRoom(): string {
    let textToReturn: string;
    switch (this.dataToInsert.currentQuote?.packageContent?.typology) {
      case 'Holiday':
        textToReturn = this.dataToInsert.currentQuote?.packageContent?.roomTypology;
        break;
      case 'Tour':
        textToReturn = 'N/D';
        break;

    }
    return textToReturn;
  }

  private formatProfileDataForDem(profileData: Agenzia): IRemoteProfile {
    const profileDataFormatted = JSON.parse(JSON.stringify(profileData));

    profileDataFormatted.ragione_sociale = profileDataFormatted.businessName;
    profileDataFormatted.denominazione = profileDataFormatted.denomination;
    profileDataFormatted.indirizzo = profileDataFormatted.address;
    profileDataFormatted.citta = profileDataFormatted.city;
    profileDataFormatted.provincia = profileDataFormatted.province;
    profileDataFormatted.email_1 = profileDataFormatted.email1;
    profileDataFormatted.email_2 = profileDataFormatted.email2;
    profileDataFormatted.tel_1 = profileDataFormatted.tel1;
    profileDataFormatted.tel_2 = profileDataFormatted.tel2;
    profileDataFormatted.sito = profileDataFormatted.site;

    delete profileDataFormatted.businessName;
    delete profileDataFormatted.denomination;
    delete profileDataFormatted.address;
    delete profileDataFormatted.city;
    delete profileDataFormatted.province;
    delete profileDataFormatted.email1;
    delete profileDataFormatted.email2;
    delete profileDataFormatted.tel1;
    delete profileDataFormatted.tel2;
    delete profileDataFormatted.site;
    return profileDataFormatted;
  }

  private insertHolidayCharges(layoutDOM: Document, chargesArrays: DemChargeArray[]) {
    // chargesArrays dovrebbe avere come elementi charges da pagare subito ed eventualmente charges da pagare in loco
    for (const chargeArray of chargesArrays) {
      const chargeSection = layoutDOM.getElementById(`${chargeArray.chargesKey}-section`);
      if (!chargeArray?.items?.length) {
        // Se non ci sno charges di questo tipo rimuove intera sezione
        chargeSection?.remove();
        continue;
      }
      const valueAsString = (value: number) => this._currencyPipe.transform(value, '€', 'symbol', '1.2-2', 'it');
      const { chargesKey, items } = chargeArray;
      // Totale per tipologia di charges
      layoutDOM.getElementById(`${chargesKey}-total`).innerHTML = valueAsString(chargeArray.total);
      const singleChargeTr = layoutDOM.getElementById(`${chargesKey}-row`);
      items
        ?.forEach(({ value, description }) => {
          const currentChargeHtml = singleChargeTr.innerHTML
            .replace(`id="${chargesKey}-row"`, '')
            .replace(`[[${chargesKey}.description]]`, description)
            .replace(`[[${chargesKey}.value]]`, valueAsString(value));
          singleChargeTr.insertAdjacentHTML('beforebegin', currentChargeHtml);
        });
      singleChargeTr.remove();
    }
    return layoutDOM;
  }

  public replaceHolidayDemPlaceholders(layoutHtml: string, demObj: any): string {
    const layoutDOM = new DOMParser().parseFromString(layoutHtml, 'text/html');
    // Se non è stato passato il link alla proposta di vendita, rimuovo l'intero noto
    if (!demObj?.propostaUrl?.length) {
      layoutDOM.getElementById('propostaVenditaUrlTable')?.remove();
    }

    if (demObj.selectedOperation.operation !== QuotationOperationType.BKD) {
      layoutDOM.getElementById('total-payed')?.remove();
    }

    if(demObj.selectedOperation.operation === QuotationOperationType.PREVENTIVO) {
      layoutDOM.getElementById('title-booking')?.remove();
      layoutDOM.getElementById('booking-code-bkd')?.remove();
      layoutDOM.getElementById('header-bkd')?.remove();
    } else {
      layoutDOM.getElementById('header-preventive')?.remove();
      layoutDOM.getElementById('booking-code-preventive')?.remove();
    }

    if (demObj.selectedOperation.operation !== QuotationOperationType.OPTION) {
      layoutDOM.getElementById('payed')?.remove();
      layoutDOM.getElementById('debt')?.remove();
      layoutDOM.getElementById('debt-copy')?.remove();
    }
    // A seconda del tipo di viaggio, rimuovo le parti che non dovranno essere mostrate
    // l'effettiva sostituzione dei placeholder avverrà in un secondo momento
    if (demObj.transport?.isSoloLand) {
      // se è solo-soggiorno rimuovo l'intero blocco voli
      layoutDOM.getElementById('flights')?.remove();
    } else {
      // altrimenti rimuovo item soloLand
      layoutDOM.getElementById('sololand')?.remove();
      if (!demObj.transport?.stopOverPlaceName?.length) {
        // se non c'è scalo rimuovo anche item stopOver
        layoutDOM.getElementById('stopOver')?.remove();
      }
    }
    layoutHtml = this.insertFlightsData(layoutDOM, demObj.operatives)?.body.innerHTML;
    layoutHtml = this.insertPassengersData(layoutDOM, demObj.demPassengers)?.body?.innerHTML;
    layoutHtml = this.insertHolidayCharges(layoutDOM, demObj.charges)?.body?.innerHTML;
    // Sostituzione placeholders
    const regexpBrackets = new RegExp(/({{)(.*?)(}})/g);
    const placeholders = layoutHtml.match(regexpBrackets);
    try {
      placeholders?.forEach((placeholder: any) => {
        const placeholderSanitized = `{{${placeholder.replace(new RegExp(/[{{]|[}}]/g), '')}}}`;
        layoutHtml = layoutHtml.replace(
          placeholderSanitized,
          this.getHolidayReplaceText(placeholderSanitized, demObj)
        );
      });
    } catch (err) {
      console.error('Errore nella sostituzione dei placeholders', err);
    }
    return layoutHtml;
  }

  public replaceCruiseDemPlaceholders(layoutHtml: string, demObj: any): string {
    const layoutDOM = new DOMParser().parseFromString(layoutHtml, 'text/html');

    if(![QuotationOperationType.BKD, QuotationOperationType.OPTION].includes(demObj.selectedOperation)) {
      layoutDOM.getElementById('description-bkd')?.remove();
    }

    if (demObj.selectedOperation !== QuotationOperationType.BKD) {
      layoutDOM.getElementById('total-payed')?.remove();
    }

    if (demObj.selectedOperation === QuotationOperationType.PREVENTIVO) {
      layoutDOM.getElementById('title-booking')?.remove();
      layoutDOM.getElementById('option-code')?.remove();
      layoutDOM.getElementById('text-bkd')?.remove();
      layoutDOM.getElementById('text-option')?.remove();
      layoutDOM.getElementById('booking-code-bkd')?.remove();
      layoutDOM.getElementById('header-bkd')?.remove();
    } else {
      layoutDOM.getElementById('header-preventive')?.remove();
      layoutDOM.getElementById('booking-code-preventive')?.remove();
    }

    if (demObj.selectedOperation !== QuotationOperationType.OPTION) {
      layoutDOM.getElementById('payed')?.remove();
      layoutDOM.getElementById('debt')?.remove();
      layoutDOM.getElementById('debt-copy')?.remove();
    }

    if (!demObj?.propostaUrl?.length) {
      layoutDOM.getElementById('propostaVenditaUrlTable')?.remove();
      layoutDOM.getElementById('text-bkd')?.remove();
    }

    if (demObj.selectedOperation !== QuotationOperationType.OPZIONAMENTO) {
      layoutDOM.getElementById('expiration-option')?.remove();
      layoutDOM.getElementById('text-option')?.remove();
      layoutDOM.getElementById('header-block-booking')?.remove();
      layoutDOM.getElementById('description-block-booking')?.remove();
      layoutDOM.getElementById('title-blocked')?.remove();
    } else {
      layoutDOM.getElementById('title-booking')?.remove();
      layoutDOM.getElementById('header-bkd')?.remove();
      layoutDOM.getElementById('header-preventive')?.remove();
      layoutDOM.getElementById('templatePreheader')?.remove();
      layoutDOM.getElementById('custom-copy')?.remove();
      layoutDOM.getElementById('booking-code')?.remove();
    }

    let resultHtml = layoutDOM.documentElement.outerHTML;

    Object.keys(demObj.placeHolders).forEach(key => {
      const placeholder = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      resultHtml = resultHtml.replace(placeholder, demObj.placeHolders[key]);
    });

    return resultHtml;
  }

  private getHolidayReplaceText(path: string, demObj: any): string {
    const holidayDetails: IHolidayDetail = demObj.holidayDetails;
    const holidayLiveSolution: IHolidayLiveSolution = demObj?.holidayLive?.solutions[0];
    const holidayDetailSolution: IHolidayDetailSolution = demObj?.selectedSolution;
    const customer = demObj.demPassengers[0];
    const profile: Agenzia = demObj.agencyInfo;
    const tourOperatorId = holidayDetails.tourOperatorId;

    switch (path) {
      case '{{currentQuote.customer[0].name}}':
        return customer.name;
      case '{{currentQuote.customer[0].surname}}':
        return customer.surname;
      case '{{currentQuote.id}}':
        return '##bookingNumber##';
      case '{{currentQuote.packageContent.days}}':
        return (holidayLiveSolution?.nights + 1)?.toString();
      case '{{currentQuote.packageContent.demCoverImage}}':
        return demObj.accommodation?.image;
      case '{{currentQuote.packageContent.departureDateFrom}}':
        return dayjs(holidayDetailSolution?.dateFrom).format('DD/MM/YYYY');
      case '{{currentQuote.packageContent.departureDateTo}}':
        return dayjs(holidayDetailSolution?.dateTo).format('DD/MM/YYYY');
      case '{{departurePlaceName}}':
        return demObj?.transport?.departurePlaceName;
      case '{{arrivalPlaceName}}':
        return demObj?.transport?.arrivalPlaceName;
      case '{{stopOverPlaceName}}':
        return demObj.transport?.stopOverPlaceName// TODO
      case '{{destination}}':
        return demObj.accommodation?.destinations;
      case '{{currentQuote.packageContent.nights}}':
        return holidayLiveSolution.nights?.toString();
      case "{{titleExtended}}":
        return demObj.accommodation?.titleExtended;
      case '{{accommodationName}}':
        return demObj.accommodation?.name;
      case '{{currentYear}}':
        return new Date().getFullYear().toString();
      case '{{designSystem}}':
        return this.designSystem;
      case '{{profileData.citta}}':
        return profile?.city;
      case '{{profileData.email1}}':
        return profile?.email1;
      case '{{profileData.indirizzo}}':
        return profile?.address;
      case '{{profileData.piva}}':
        return profile?.piva;
      case '{{profileData.provincia}}':
        return profile?.province;
      case '{{profileData.ragione_sociale}}':
        return profile?.businessName;
      case '{{profileData.denominazione}}':
        return profile?.denomination;
      case '{{profileData.sito}}':
        return profile?.site;
      case '{{profileData.tel1}}':
        return profile?.tel1;
      case '{{agencyLogo}}':
        return profile?.logo;
      case '{{this.getRoom()}}':
        return demObj.selectedRoom?.title;
      case '{{tourOperatorLogo}}':
        return `https://cdn.otosrl.com/images/touroperator/mini/${tourOperatorId}-${tourOperatorId}.png`;
      case '{{this.getTreatment()}}':
        return demObj.selectedRoom?.basis?.name;
      case '{{propostaVenditaUrl}}':
        return demObj.propostaUrl;
      case '{{payed}}':
        return Number(demObj.quotation.availableOperations.find(x => x.operation === "OPTION")?.value)?.toFixed(2)?.replace('.', ',');
      case '{{payed}}':
        return Number(demObj.quotation?.availableOperations?.find(x => x.operation === "OPTION")?.value)?.toFixed(2)?.replace('.', ',');
      case '{{debt}}':
        return this.calculateDebt(demObj?.quotation?.availableOperations);
      case '{{totalPayed}}':
        return demObj?.charges[0]?.total
      default:
        return '';
    }
  }

  private calculateDebt(option): string {
    return (option?.find(x => x.operation === "BKD")?.value - option?.find(x => x.operation === "OPTION")?.value)?.toFixed(2)?.replace('.', ',');
  }
}
